import { AlertTriangle, Plus, UserX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { InfoNote } from '@/components/common/Panel'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { PROJECT_WORK_PERM } from '../../permissions'
import { useAssignTask } from '../../taskApi'
import { isOverdue } from '../../taskFilters'
import type { Project, Task } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { DisciplineBadge } from '../ProjectBits'
import { PriorityBadge, TaskStatusBadge } from '../StatusBadges'
import type { TaskDefaults } from '../task/TaskFormModal'

export function AssignmentTab({ project, onNewTask }: { project: Project; onNewTask: (d?: TaskDefaults) => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { nameOf } = useStaffLookup()
  const assign = useAssignTask(project.id)
  const canWrite = can(PROJECT_WORK_PERM)

  const open = project.tasks.filter((x) => x.status !== 'done')
  const unassigned = open.filter((x) => !x.assigneeId)
  const people = [{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId) }))]
  const maxLoad = Math.max(1, ...project.team.map((m) => open.filter((x) => x.assigneeId === m.userId).length))

  const row = (task: Task) => (
    <li key={task.id} className="flex flex-col gap-2 py-2.5 @2xl:flex-row @2xl:items-center @2xl:gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="w-16 shrink-0 font-mono text-xs text-primary">{task.key}</span>
        <Link to={`/projects/${project.id}/tasks/${task.id}`} className="min-w-0 flex-1 truncate text-sm font-medium hover:text-primary hover:underline">
          {task.title}
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <PriorityBadge priority={task.priority} compact />
        <TaskStatusBadge status={task.status} />
        <span className={cn('ml-auto text-xs tabular-nums text-muted-foreground @2xl:ml-0 @2xl:w-24 @2xl:text-right', isOverdue(task) && 'font-medium text-destructive')}>{fmtDate(task.dueDate)}</span>
      </div>
      {canWrite ? (
        <Select
          size="xs"
          className="w-full @2xl:w-40"
          value={task.assigneeId ?? ''}
          options={people}
          aria-label={t('projects.assign.reassign', { key: task.key })}
          onChange={(v) => assign.mutate({ taskId: task.id, assigneeId: v || null }, { onError: toastError })}
        />
      ) : null}
    </li>
  )

  return (
    <div className="space-y-4">
      <InfoNote>{t('projects.assign.hint')}</InfoNote>

      <section className="@container rounded-xl border border-warning/30 bg-card shadow-card">
        <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          <UserX className="size-4 text-warning" aria-hidden />
          <h3 className="font-semibold">{t('projects.assign.unassigned', { count: unassigned.length })}</h3>
          {canWrite ? (
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => onNewTask()}>
              <Plus />
              {t('projects.tasks.add')}
            </Button>
          ) : null}
        </header>
        {unassigned.length ? <ul className="divide-y px-4">{unassigned.map(row)}</ul> : <p className="px-4 py-6 text-center text-sm text-muted-foreground">{t('projects.assign.allAssigned')}</p>}
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {project.team.map((m) => {
          const mine = open.filter((x) => x.assigneeId === m.userId)
          const overdue = mine.filter(isOverdue).length
          const hours = mine.reduce((sum, x) => sum + Math.max(0, x.estimateHours - x.spentHours), 0)
          return (
            <section key={m.id} className="@container rounded-xl border bg-card shadow-card">
              <header className="flex items-center gap-3 border-b px-4 py-3">
                <Avatar name={nameOf(m.userId)} className="size-10" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{nameOf(m.userId)}</p>
                  <p className="truncate text-xs text-muted-foreground">{m.title}</p>
                </div>
                <DisciplineBadge discipline={m.discipline} />
              </header>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-xs text-muted-foreground">
                <span>{t('projects.assign.openCount', { count: mine.length })}</span>
                <span>{t('projects.assign.remainingHours', { count: hours })}</span>
                {overdue ? (
                  <span className="inline-flex items-center gap-1 font-medium text-destructive">
                    <AlertTriangle className="size-3.5" aria-hidden />
                    {t('projects.assign.overdue', { count: overdue })}
                  </span>
                ) : null}
                <div className="h-1.5 min-w-24 flex-1 overflow-hidden rounded-full bg-muted" role="meter" aria-valuenow={mine.length} aria-valuemin={0} aria-valuemax={maxLoad} aria-label={t('projects.assign.load')}>
                  <div className={cn('h-full rounded-full', mine.length / maxLoad > 0.8 ? 'bg-warning' : 'bg-primary')} style={{ width: `${(mine.length / maxLoad) * 100}%` }} />
                </div>
                {canWrite ? (
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => onNewTask({ assigneeId: m.userId })} aria-label={t('projects.assign.newFor', { name: nameOf(m.userId) })}>
                    <Plus />
                  </Button>
                ) : null}
              </div>
              {mine.length ? <ul className="divide-y border-t px-4">{mine.map(row)}</ul> : null}
            </section>
          )
        })}
      </div>
    </div>
  )
}
